import {
  cloneElement,
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from './Button.jsx';
import SlideOutHeader, { PanelCollapseToggle } from './SlideOutHeader.jsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// ─── Module constants ─────────────────────────────────────────────────────────

const HAS_DOCUMENT = typeof document !== 'undefined';

// ─── Scroll lock ──────────────────────────────────────────────────────────────

const scrollLockMap = new WeakMap();

const lockScroll = (target) => {
  const state = scrollLockMap.get(target);
  if (state) {
    state.count += 1;
    return;
  }
  scrollLockMap.set(target, { count: 1, previousOverflow: target.style.overflow });
  target.style.overflow = 'hidden';
};

const unlockScroll = (target) => {
  const state = scrollLockMap.get(target);
  if (!state) return;
  state.count -= 1;
  if (state.count > 0) return;
  target.style.overflow = state.previousOverflow;
  scrollLockMap.delete(target);
};

// ─── Panel stack (Escape key guard) ───────────────────────────────────────────

const openPanelStack = [];

const pushPanel = (id) => {
  if (!openPanelStack.includes(id)) openPanelStack.push(id);
};

const popPanel = (id) => {
  const idx = openPanelStack.lastIndexOf(id);
  if (idx >= 0) openPanelStack.splice(idx, 1);
};

const isTopPanel = (id) => openPanelStack[openPanelStack.length - 1] === id;

// ─── Focus trap ───────────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ─── Size config ──────────────────────────────────────────────────────────────

// `collapsible` variant: 50px rail when collapsed. Uses a shipped utility class
// (see aimsos-ui-animations.css) for an exact, non-purgeable width that also
// resists flex-shrink — arbitrary Tailwind widths proved unreliable here.
const COLLAPSED_WIDTH_CLASS = 'slideout-collapsed-rail';

const PANEL_SIZE_M = {
  widthClass: 'w-[635px]',
  radiusClass: { right: 'rounded-tl-[24px] rounded-bl-[24px]', left: 'rounded-tr-[24px] rounded-br-[24px]' },
  paddingClass: 'px-6 py-8',
  gapClass: 'gap-8',
  btnSize: 'md',
};

const PANEL_SIZE = {
  M: PANEL_SIZE_M,
  S: {
    widthClass: 'w-[420px]',
    radiusClass: { right: 'rounded-tl-[16px] rounded-bl-[16px]', left: 'rounded-tr-[16px] rounded-br-[16px]' },
    paddingClass: 'px-6 py-6',
    gapClass: 'gap-4',
    btnSize: 'sm',
  },
  // `custom` mirrors M for padding/radius/gap/button; width comes from `customWidth`.
  custom: { ...PANEL_SIZE_M, widthClass: null },
};

// Resolves the `customWidth` prop into a Tailwind width class. Ready-made classes
// ('w-[720px]', 'w-1/2') pass through; a bare number or px value (300, '300',
// '300px') becomes `w-[300px]` (arbitrary px 40–1000 are covered by the safelist).
const resolveCustomWidthClass = (customWidth) => {
  if (customWidth == null || customWidth === '') return '';
  if (typeof customWidth === 'number') return `w-[${customWidth}px]`;
  const trimmed = String(customWidth).trim();
  if (!trimmed) return '';
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `w-[${trimmed}px]`;
  if (/^\d+(\.\d+)?px$/.test(trimmed)) return `w-[${trimmed}]`;
  return trimmed;
};

// ─── Position config ──────────────────────────────────────────────────────────

const PANEL_POSITION = {
  right: {
    sideClass: 'right-0 top-0',
    shadowClass: 'shadow-[-24px_-24px_60px_0px_rgba(0,0,0,0.08)]',
    animationClass: 'animate-slideout-in-right',
  },
  left: {
    sideClass: 'left-0 top-0',
    shadowClass: 'shadow-[24px_-24px_60px_0px_rgba(0,0,0,0.08)]',
    animationClass: 'animate-slideout-in-left',
  },
};

// ─── Internal hooks ───────────────────────────────────────────────────────────

const useScrollLock = (target, isActive) => {
  useEffect(() => {
    if (!isActive || !target) return undefined;
    lockScroll(target);
    return () => unlockScroll(target);
  }, [isActive, target]);
};

const usePanelStack = (id, isActive) => {
  useEffect(() => {
    if (!isActive) return undefined;
    pushPanel(id);
    return () => popPanel(id);
  }, [isActive, id]);
};

// Escape-to-close for the non-modal variant (`showBackdrop={false}`), which
// skips the full focus trap. Kept stack-aware so only the top-most panel closes.
const useEscapeToClose = (isActive, onCloseRef, instanceId) => {
  useEffect(() => {
    if (!isActive) return undefined;
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (!isTopPanel(instanceId)) return;
      onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, instanceId, onCloseRef]);
};

const useFocusTrap = (panelRef, isActive, onCloseRef, instanceId) => {
  const previousActiveRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    previousActiveRef.current = HAS_DOCUMENT ? document.activeElement : null;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (!isTopPanel(instanceId)) return;
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const panel = panelRef.current;
      if (!panel.contains(document.activeElement)) return;

      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) =>
          el.offsetParent !== null &&
          !el.hasAttribute('disabled') &&
          el.getAttribute('aria-hidden') !== 'true',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    const focusFrame = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable[0] && typeof focusable[0].focus === 'function') {
        focusable[0].focus();
      }
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      const prev = previousActiveRef.current;
      if (prev && typeof prev.focus === 'function') {
        try {
          if (document.body.contains(prev)) prev.focus();
        } catch { /* ignore */ }
      }
    };
  }, [isActive, instanceId, onCloseRef, panelRef]);
};

// ─── ChipButton ───────────────────────────────────────────────────────────────

const ChipButton = memo(({ value, label, active, onSelect }) => {
  const handleClick = useCallback(() => onSelect?.(value), [onSelect, value]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        'shrink-0 flex items-center justify-center px-3 py-1 rounded-full',
        'text-[14px] leading-[20px] font-medium transition-colors',
        active
          ? 'bg-[#2173FF] text-white'
          : 'bg-white dark:bg-[#1E2535] border border-[#5C5C5C] dark:border-[#3A3A3A] text-[#2A2A2A] dark:text-[#94A3B8]',
      ].filter(Boolean).join(' ')}
    >
      {label}
    </button>
  );
});

ChipButton.displayName = 'ChipButton';
ChipButton.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onSelect: PropTypes.func,
};

// ─── Collapse header wiring ───────────────────────────────────────────────────

const isCollapseHeaderVariant = (headerNode) =>
  headerNode != null
  && !Array.isArray(headerNode)
  && headerNode.props?.variant === 'collapse';

/** Wire collapse state into `<SlideOut.Header variant="collapse">`. */
const injectCollapseHeaderProps = (headerNode, collapseProps) => {
  if (!headerNode || !isCollapseHeaderVariant(headerNode)) return headerNode;
  return cloneElement(headerNode, collapseProps);
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Side panel that slides in from the right (or left via `position="left"`).
 * Portals to `document.body` by default; pass `portalTarget` to render inside
 * a specific container.
 *
 * Layout (top → bottom):
 *   header   — pass via `header` prop (use `<SlideOut.Header>`)
 *   chips    — optional category chip row (`showChips + chips`)
 *   children — flex-1 scrollable content area
 *   cta      — optional footer buttons (`showCta + cta`)
 *
 * Use the `renderHeader` render prop to receive the auto-generated `titleId`
 * for correct `aria-labelledby` wiring:
 *   renderHeader={(titleId) => <SlideOut.Header titleId={titleId} title="…" />}
 *
 * Set `showBackdrop={false}` for a non-modal panel: no dimmed backdrop, no
 * scroll lock, and no focus trap, so the content behind stays visible and
 * interactive (Escape still closes). The host is responsible for its own
 * dismiss-on-outside-click. Defaults to `true` (modal).
 *
 * `layout="overlay"` (default) portals the panel and overlays it above the page
 * (fixed/absolute). `layout="inline"` renders the panel in place within its
 * parent (no portal, no backdrop, `position: relative`, full parent height), so
 * it occupies space in the flow and pushes sibling content when opened. Give the
 * parent a flex/grid layout for the push effect.
 *
 * `size="custom"` takes the width from `customWidth`, which is a Tailwind width
 * class (`'w-[720px]'`, `'w-1/2'`) or a bare number (`300` → `w-[300px]`);
 * when omitted, falls back to the M preset width. Ignored for the `M` and `S`
 * presets. Arbitrary px in the 40–1000 range are pre-generated via the package
 * Tailwind safelist. Custom widths are capped at half the available space
 * (`50vw` overlay, `50%` inline); M/S presets are not.
 *
 * `noPadding={true}` removes the built-in size-preset padding and gap so the
 * panel's children can fill edge-to-edge (useful when the child owns its own
 * header, padding, and scroll regions, e.g. a trace panel or a catalog sidebar).
 *
 * `variant="full"` (default) is the standard panel. `variant="collapsible"` adds
 * a collapsed state: instead of hiding, the panel shrinks to a 50px rail that
 * renders `collapsedContent` (icon + action) — `children` are shown only when
 * expanded. In this variant the panel renders its own expand/collapse toggle
 * (lucide `Panel*Open/Close`) instead of a close button; hide the header close.
 * Control it with `collapsed` + `onCollapsedChange`, or leave it uncontrolled
 * (seeded by `defaultCollapsed`).
 */
const SlideOut = ({
  isOpen,
  onClose,
  type = 'with-variants',
  size = 'M',
  customWidth,
  layout = 'overlay',
  position = 'right',
  variant = 'full',
  collapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  collapsedContent,
  collapseAriaLabel = 'Collapse panel',
  expandAriaLabel = 'Expand panel',
  header,
  renderHeader,
  children,
  chips,
  showChips = false,
  cta,
  showCta = false,
  showScrollbar = false,
  portalTarget,
  ariaLabel = 'Slide-out panel',
  chipsScrollLeftAriaLabel = 'Scroll chips left',
  chipsScrollRightAriaLabel = 'Scroll chips right',
  backdropZClassName = 'z-[100]',
  panelZClassName = 'z-[101]',
  closeOnBackdrop = true,
  showBackdrop = true,
  noPadding = false,
}) => {
  const instanceId = useId();
  const titleId = useId();
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useLayoutEffect(() => { onCloseRef.current = onClose; });

  const chipsScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateChipsScroll = useCallback(() => {
    const el = chipsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scrollLeft = useCallback(() => {
    chipsScrollRef.current?.scrollBy({ left: -160, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    chipsScrollRef.current?.scrollBy({ left: 160, behavior: 'smooth' });
  }, []);

  // Collapsed state: controlled via `collapsed`, otherwise internal (uncontrolled).
  const isCollapsible = variant === 'collapsible';
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsedControlled = collapsed !== undefined;
  const isCollapsed = isCollapsible && (isCollapsedControlled ? collapsed : internalCollapsed);

  const toggleCollapsed = useCallback(() => {
    const next = !isCollapsed;
    if (!isCollapsedControlled) setInternalCollapsed(next);
    onCollapsedChange?.(next);
  }, [isCollapsed, isCollapsedControlled, onCollapsedChange]);

  const cfg = PANEL_SIZE[size] ?? PANEL_SIZE.M;
  const posCfg = PANEL_POSITION[position] ?? PANEL_POSITION.right;
  const radiusClass = cfg.radiusClass[position] ?? cfg.radiusClass.right;

  const isInline = layout === 'inline';

  // `custom` size drives width via a Tailwind class (no inline style).
  const customWidthClass = useMemo(
    () => (size === 'custom' ? resolveCustomWidthClass(customWidth) : ''),
    [size, customWidth],
  );

  const resolvedPortalTarget = useMemo(() => {
    if (isInline || !HAS_DOCUMENT) return null;
    return portalTarget instanceof HTMLElement ? portalTarget : document.body;
  }, [isInline, portalTarget]);

  const isBodyPortal = HAS_DOCUMENT && resolvedPortalTarget === document.body;
  // Inline: in-flow (`relative`) so it takes space and pushes siblings.
  const layerPositionClass = isInline ? 'relative' : (isBodyPortal ? 'fixed' : 'absolute');
  const resolvedHeight = isInline || !isBodyPortal ? 'h-full' : 'h-screen';

  // Inline panels and the collapsed rail are never modal: no backdrop, scroll
  // lock, focus trap or Escape.
  const effectiveShowBackdrop = showBackdrop && !isInline && !isCollapsed;
  // Portaled but non-modal (`showBackdrop={false}`): keep only Escape-to-close.
  const isNonModalOverlay = !isInline && !showBackdrop && !isCollapsed;

  // chips?.length as dep: we only care if the number of chips changed (for scroll recalc)
  const chipsLength = chips?.length ?? 0;
  useEffect(() => {
    if (isOpen) updateChipsScroll();
  }, [isOpen, chipsLength, updateChipsScroll]);

  // Modal-only behaviors (backdrop dim, scroll lock, focus trap) are gated on
  // `effectiveShowBackdrop`; the non-modal overlay keeps only a stack-aware
  // Escape close. Inline panels opt out of all of these.
  // Collapsed overlay rails skip the stack so they don't block Escape on panels below.
  const participatesInPanelStack = isOpen && !isInline && !isCollapsed;
  useScrollLock(resolvedPortalTarget, isOpen && effectiveShowBackdrop);
  usePanelStack(instanceId, participatesInPanelStack);
  useFocusTrap(panelRef, isOpen && effectiveShowBackdrop, onCloseRef, instanceId);
  useEscapeToClose(isOpen && isNonModalOverlay, onCloseRef, instanceId);

  const handleBackdropClick = useCallback(
    (e) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onCloseRef.current?.();
    },
    [closeOnBackdrop],
  );

  const hasChips = showChips && type === 'with-variants' && Array.isArray(chips) && chips.length > 0;
  const hasCta = showCta && !!cta;
  const resolvedHeader = renderHeader ? renderHeader(titleId) : header;
  const usesCollapseHeader = isCollapseHeaderVariant(resolvedHeader);
  const collapseHeaderProps = useMemo(
    () => ({
      isCollapsed,
      onCollapseToggle: toggleCollapsed,
      position,
      collapseAriaLabel,
      expandAriaLabel,
    }),
    [collapseAriaLabel, expandAriaLabel, isCollapsed, position, toggleCollapsed],
  );
  const enhancedHeader = useMemo(
    () => injectCollapseHeaderProps(resolvedHeader, collapseHeaderProps),
    [resolvedHeader, collapseHeaderProps],
  );

  // When collapsed: 50px rail, minimal padding. Otherwise the resolved width.
  // `size="custom"` without `customWidth` falls back to the M preset width.
  const widthClass = isCollapsed
    ? COLLAPSED_WIDTH_CLASS
    : (customWidthClass || cfg.widthClass || PANEL_SIZE_M.widthClass);
  const paddingClass = isCollapsed ? 'px-0 py-4' : (noPadding ? '' : cfg.paddingClass);
  const gapClass = isCollapsed ? 'gap-4' : (noPadding ? '' : cfg.gapClass);

  if (!isOpen) return null;
  // Overlay needs a portal target and a document; inline renders in place.
  if (!isInline && (!HAS_DOCUMENT || !resolvedPortalTarget)) return null;

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={effectiveShowBackdrop ? 'true' : undefined}
      aria-labelledby={titleId}
      aria-label={ariaLabel}
      className={[
        layerPositionClass,
        // Inline: hold width (don't let flex shrink it), order within the flex
        // parent per `position`, and animate the width-based push.
        isInline && 'shrink-0',
        isInline && (position === 'left' ? 'order-first' : 'order-last'),
        isInline && 'animate-slideout-inline',
        // z-index and off-screen slide animation only matter for overlay panels.
        !isInline && panelZClassName,
        !isInline && posCfg.sideClass,
        resolvedHeight,
        // Collapsed rail (50px), custom width, or the preset's fixed width.
        widthClass,
        // Cap custom widths at half the available space; M/S presets keep their
        // fixed widths on narrow viewports (no regression).
        size === 'custom' && !isCollapsed && (isInline ? 'max-w-[50%]' : 'max-w-[50vw]'),
        // Smooth expand/collapse in the collapsible variant.
        isCollapsible && 'transition-[width] duration-300 ease-out',
        radiusClass,
        paddingClass,
        gapClass,
        'flex flex-col',
        'bg-white/[0.92] dark:bg-[#131722]/[0.92]',
        'backdrop-blur-[30px]',
        // Border to delineate the panel edge — the light drop shadow barely reads
        // in dark mode, and the collapsible rail has no backdrop to separate it.
        'border border-black/10 dark:border-white/15',
        posCfg.shadowClass,
        !isInline && posCfg.animationClass,
        showScrollbar ? 'overflow-y-auto scrollbar-aimsos' : 'overflow-hidden',
      ]
        .filter(Boolean)
        .join(' ')}
    >
        {isCollapsed ? (
          /* Collapsed rail: collapse header (expand only) or toggle + collapsed content */
          <div className="flex h-full flex-col items-center gap-4">
            {usesCollapseHeader ? (
              enhancedHeader
            ) : (
              <PanelCollapseToggle
                isCollapsed={isCollapsed}
                position={position}
                collapseAriaLabel={collapseAriaLabel}
                expandAriaLabel={expandAriaLabel}
                onToggle={toggleCollapsed}
              />
            )}
            <div className="flex min-h-0 w-full flex-1 flex-col items-center">
              {collapsedContent}
            </div>
          </div>
        ) : (
        <>
        {/* Collapsible toggle — skip when header variant="collapse" owns the control */}
        {isCollapsible && !usesCollapseHeader && (
          <div
            className={[
              'flex shrink-0',
              position === 'left' ? 'justify-end' : 'justify-start',
            ].join(' ')}
          >
            <PanelCollapseToggle
              isCollapsed={isCollapsed}
              position={position}
              collapseAriaLabel={collapseAriaLabel}
              expandAriaLabel={expandAriaLabel}
              onToggle={toggleCollapsed}
            />
          </div>
        )}

        {/* Header */}
        {enhancedHeader}

        {/* Category chips — with-variants only */}
        {hasChips && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              aria-label={chipsScrollLeftAriaLabel}
              onClick={scrollLeft}
              className={[
                'shrink-0 items-center justify-center w-[28px] h-[28px] rounded p-1',
                'text-[#52525B] dark:text-[#D1D5DB] hover:bg-black/5 dark:hover:bg-white/10 transition-colors',
                canScrollLeft ? 'flex' : 'hidden',
              ].join(' ')}
            >
              <ChevronLeft size={16} />
            </button>

            <div className="relative flex-1 min-w-0">
              <div
                ref={chipsScrollRef}
                onScroll={updateChipsScroll}
                className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {chips.map((chip) => (
                  <ChipButton
                    key={chip.value}
                    value={chip.value}
                    label={chip.label}
                    active={chip.active}
                    onSelect={chip.onSelect}
                  />
                ))}
              </div>
              {/* Gradient fades — hardcoded per spec (Figma vars don't support gradients) */}
              {canScrollLeft && (
                <>
                  <div
                    className="pointer-events-none absolute left-0 top-0 h-full w-8 dark:hidden"
                    style={{ background: 'linear-gradient(to left, transparent, white)' }}
                  />
                  <div
                    className="pointer-events-none absolute left-0 top-0 h-full w-8 hidden dark:block"
                    style={{ background: 'linear-gradient(to left, transparent, #131722)' }}
                  />
                </>
              )}
              {canScrollRight && (
                <>
                  <div
                    className="pointer-events-none absolute right-0 top-0 h-full w-8 dark:hidden"
                    style={{ background: 'linear-gradient(to right, transparent, white)' }}
                  />
                  <div
                    className="pointer-events-none absolute right-0 top-0 h-full w-8 hidden dark:block"
                    style={{ background: 'linear-gradient(to right, transparent, #131722)' }}
                  />
                </>
              )}
            </div>

            <button
              type="button"
              aria-label={chipsScrollRightAriaLabel}
              onClick={scrollRight}
              className={[
                'shrink-0 items-center justify-center w-[28px] h-[28px] rounded p-1',
                'text-[#52525B] dark:text-[#D1D5DB] hover:bg-black/5 dark:hover:bg-white/10 transition-colors',
                canScrollRight ? 'flex' : 'hidden',
              ].join(' ')}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Content area */}
        <div
          className={[
            'flex-1 min-h-0',
            !showScrollbar ? 'overflow-y-auto scrollbar-aimsos' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {children ?? (
            <div className="flex h-full items-center justify-center border border-dashed border-[#80AFFF] rounded-lg">
              <span className="text-[#2173FF] text-[18px] font-semibold">
                Replace content here
              </span>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        {hasCta && (
          <div className="shrink-0 flex items-center justify-end gap-2">
            {cta.secondary && (
              <Button variant="secondary" size={cfg.btnSize} onClick={cta.secondary.onClick}>
                {cta.secondary.label}
              </Button>
            )}
            {cta.primary && (
              <Button variant="classicPrimary" size={cfg.btnSize} onClick={cta.primary.onClick}>
                {cta.primary.label}
              </Button>
            )}
          </div>
        )}
        </>
        )}
    </div>
  );

  // Inline: render in place (no portal, no backdrop) so it stays in the flow.
  if (isInline) return panel;

  return createPortal(
    <>
      {/* Backdrop — omitted in the non-modal variant */}
      {effectiveShowBackdrop && (
        <div
          className={[layerPositionClass, 'inset-0 bg-black/40', backdropZClassName]
            .filter(Boolean)
            .join(' ')}
          onClick={handleBackdropClick}
          role="presentation"
        />
      )}
      {panel}
    </>,
    resolvedPortalTarget,
  );
};

// ─── Static sub-components ────────────────────────────────────────────────────

SlideOut.Header = SlideOutHeader;

// ─── PropTypes ────────────────────────────────────────────────────────────────

SlideOut.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['with-variants', 'full-slot']),
  size: PropTypes.oneOf(['M', 'S', 'custom']),
  /** Width when `size="custom"`: a Tailwind width class (`'w-[720px]'`, `'w-1/2'`) or a bare number (`300` → `w-[300px]`). Falls back to M width when omitted. Ignored for `M`/`S`. Capped at half the available space. */
  customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** `overlay` (default) portals + overlays the panel; `inline` renders it in-flow and pushes sibling content. */
  layout: PropTypes.oneOf(['overlay', 'inline']),
  /** `full` (default) is the standard panel; `collapsible` adds a 50px collapsed rail with its own expand/collapse toggle. */
  variant: PropTypes.oneOf(['full', 'collapsible']),
  /** Controlled collapsed state (`collapsible` only). Omit for uncontrolled behavior. */
  collapsed: PropTypes.bool,
  /** Initial collapsed state when uncontrolled (`collapsible` only). Defaults to `false`. */
  defaultCollapsed: PropTypes.bool,
  /** Called with the next collapsed state when the toggle is clicked. */
  onCollapsedChange: PropTypes.func,
  /** Content rendered in the 50px rail when collapsed (icon + action). `collapsible` only. */
  collapsedContent: PropTypes.node,
  /** Accessible label for the toggle when expanded (click collapses). */
  collapseAriaLabel: PropTypes.string,
  /** Accessible label for the toggle when collapsed (click expands). */
  expandAriaLabel: PropTypes.string,
  /** Side the panel slides in from. Defaults to `right`. */
  position: PropTypes.oneOf(['left', 'right']),
  /** Static header node. Use `renderHeader` instead to receive the auto-generated `titleId`. */
  header: PropTypes.node,
  /** Render prop: `(titleId: string) => ReactNode`. Preferred over `header` for aria wiring. */
  renderHeader: PropTypes.func,
  children: PropTypes.node,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      active: PropTypes.bool,
      onSelect: PropTypes.func,
    }),
  ),
  showChips: PropTypes.bool,
  cta: PropTypes.shape({
    primary: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
    secondary: PropTypes.shape({ label: PropTypes.string, onClick: PropTypes.func }),
  }),
  showCta: PropTypes.bool,
  showScrollbar: PropTypes.bool,
  portalTarget: PropTypes.instanceOf(typeof Element !== 'undefined' ? Element : Object),
  ariaLabel: PropTypes.string,
  chipsScrollLeftAriaLabel: PropTypes.string,
  chipsScrollRightAriaLabel: PropTypes.string,
  backdropZClassName: PropTypes.string,
  panelZClassName: PropTypes.string,
  closeOnBackdrop: PropTypes.bool,
  /** Render the dimmed modal backdrop (with scroll lock + focus trap). Set `false` for a non-modal panel. Defaults to `true`. */
  showBackdrop: PropTypes.bool,
  /** Remove built-in padding and gap so children can fill the panel edge-to-edge. */
  noPadding: PropTypes.bool,
};

export default SlideOut;
